import '../index.css'
import React, { Component } from 'react'
import CopyToClipboard from 'react-clipboard.js'
import { connect } from 'react-redux'
import request from 'superagent'
import type { Recipient } from 'types/list'
import type { User } from 'types/user'
import img1x from 'assets/images/referral.png'
import img2x from 'assets/images/referral@2x.png'
import * as FormActions from '@disco/state-management/actions/FormActions'
import { referralInvite } from '@disco/state-management/actions/ReferralInviteActions'
import mixpanel from '@disco/api/mixpanel'
import { EMAIL } from '@disco/constants/RecipientTypes'
import { keyEqualTo } from '@disco/utils/keyboard'
import { pushUINotification } from '@disco/utils/notifications'
import { validateEmail } from '@disco/utils/validation'
import RecipientList from 'components/common/email-groups/recipient-list'
import Row from 'components/common/row'
import HelpTooltip from 'components/common/tooltip'
import { DiscoModal } from 'components/ui'
import Button from 'components/ui/button'
import Heading from 'components/ui/heading'
import Hotkey from 'components/ui/hotkey'
import TextField from 'components/ui/text-field'
import modalStyles from 'components/utils/modalStyles'

function mapStateToProps(state) {
  return {
    user: state.user,
    ...state.forms.referralInviteForm,
    ...state.referralInvites,
    hostname: state.user.business.hostname.replace(/\.disco\.ac$/, ''),
  }
}

const mapDispatchToProps = { ...FormActions, referralInvite }
const ERROR_SENDING_INVITE =
  'There was an error trying to send an invite. Our team has been notified.'
type Props = {
  loading: boolean
  onClose: () => unknown
  onInviteSent: (recipients: Recipient[]) => unknown
  referralInvite: (arg0: Recipient[], arg1: string) => Promise<any>
  recipients: Recipient[]
  updateReferralInviteForm: (...args: Array<any>) => any
  resetReferralInviteForm: (...args: Array<any>) => any
  message: string
  user: User
}

class ReferralInviteSendModal extends Component<
  Props,
  {
    showCustomMessage: boolean
    isCopyStatusActive: boolean
    copyStatusText: string | null | undefined
    error: string | null | undefined
    isClicked: boolean
    recipient: {
      email: string
      type: string
      name: string
    }
  }
> {
  state = {
    showCustomMessage: false,
    isCopyStatusActive: false,
    copyStatusText: null,
    error: null,
    isClicked: false,
    recipient: {
      email: '',
      type: EMAIL,
      name: '',
    },
  }

  componentDidMount() {
    mixpanel.sendPureEvent('Open referral modal')
  }

  componentWillUnmount() {
    this.props.resetReferralInviteForm()
  }

  _handleSubmit = event => {
    const { recipients, message } = this.props
    event.preventDefault()
    this.setState({
      error: null,
    })
    this.props
      .referralInvite(recipients, message)
      .then(result => {
        if (result) {
          return this.props.onInviteSent(recipients)
        } else {
          this.setState({
            error: ERROR_SENDING_INVITE,
          })
        }
      })
      .catch(() =>
        this.setState({
          error: ERROR_SENDING_INVITE,
        }),
      )
  }
  _validateRecipient = recipient => {
    let validRecipient = recipient !== null

    if (validRecipient && (recipient.type === EMAIL || recipient.email)) {
      validRecipient = validRecipient && validateEmail(recipient.email)
    }

    return validRecipient
  }
  _checkRecipientUniqueness = recipient => {
    const items = this.props.recipients.filter(item => {
      if (recipient.type === EMAIL) return item.email === recipient.email
    })
    return items.length === 0
  }
  _changeMessage = event => {
    this.props.updateReferralInviteForm({
      message: event.target.value,
    })
  }
  _handleShowCustomMessage = event => {
    event.preventDefault()
    this.setState({
      showCustomMessage: true,
    })
  }
  _addRecipient = () => {
    const { recipient } = this.state

    const isValidRecipient = this._validateRecipient(recipient)

    const isUniqueRecipient = this._checkRecipientUniqueness(recipient)

    if (isValidRecipient && isUniqueRecipient) {
      this.setState({
        error: null,
      })
      this.props.updateReferralInviteForm({
        recipients: [...this.props.recipients, recipient],
      })

      this._setRecipientEmail('')
    } else {
      this.setState({
        error: isValidRecipient
          ? 'Enter a unique email'
          : 'Please enter valid email address',
      })
    }
  }
  _setRecipientEmail = email => {
    this.setState({
      recipient: {
        email: email,
        type: EMAIL,
        name: '',
      },
    })
  }
  _handleEmailChange = event => {
    let email = event.target.value

    this._setRecipientEmail(email)
  }
  _deleteRecipient = (recipient, index) => {
    this.props.updateReferralInviteForm({
      recipients: [
        ...this.props.recipients.slice(0, index),
        ...this.props.recipients.slice(index + 1),
      ],
    })
  }
  _renderSend = () => {
    // calculate number of recipients
    const recipients = this.props.recipients.map(item => {
      return item.type !== 'saved' ? 1 : item.recipients?.length || 0
    })
    let recipientsLength = 0

    if (recipients.length > 0) {
      recipientsLength = recipients.reduce((prev, curr) => prev + curr)
    }

    const recipientPlural = recipientsLength > 1 ? 'friends' : 'friend'
    const caption = recipientsLength
      ? `Send invite to ${recipientsLength} ${recipientPlural}`
      : 'Send invite to friends'
    return (
      <Button
        appearance="primary"
        caption={caption}
        disabled={!recipientsLength}
        onClick={this._handleSubmit}
        progressCaption="Sending"
        progressIndicator={this.props.loading}
      />
    )
  }
  _getReferralUrl = () => {
    const { user } = this.props
    const userId = user.id
    const businessId = user.business && user.business.id
    const referralObj = {
      b: businessId,
      u: userId,
    }
    return `https://disco.ac/signup?${request.serializeObject(referralObj)}`
  }
  _onCopySuccess = e => {
    mixpanel.sendPureEvent('Copy public referral link')

    this._showCopyStatus('Copied to clipboard')

    e.clearSelection()
    this.setState({
      isClicked: true,
    })
    setTimeout(() => {
      if (this)
        this.setState({
          isClicked: false,
        })
    }, 1000)
  }
  _onCopyError = () => {
    this._showCopyStatus(
      <span>
        Press <Hotkey keys={['cmd', 'C']} /> to copy
      </span>,
    )
  }
  _onKeyDown = event => {
    if (keyEqualTo(event, 'Enter')) {
      event.preventDefault()

      this._addRecipient()
    }
  }
  _showCopyStatus = (copyStatusText: any) => {
    this.setState({
      isCopyStatusActive: true,
      copyStatusText: copyStatusText,
    })
    pushUINotification({
      message: `Copied!`,
      level: 'success',
    })
  }
  _renderCustomMessage = () => {
    if (!this.state.showCustomMessage) {
      return (
        <div>
          Personalise your invite by writing{' '}
          <span
            className="Referral_Link"
            onClick={this._handleShowCustomMessage}
          >
            a message
          </span>
        </div>
      )
    }

    return (
      <Row columnsSize={[12]} marginBottom="small">
        <div>
          <TextField
            multiline={4}
            label="Add a message"
            value={this.props.message}
            onChange={this._changeMessage}
          />
        </div>
      </Row>
    )
  }

  render() {
    return (
      <DiscoModal
        onRequestClose={this.props.onClose}
        isOpen
        style={modalStyles}
      >
        <div className="modal-width--600">
          <div className="Referral_Content">
            <div className="Referral_Header">
              <div className="Referral_CloseBtn">
                <Button
                  appearance="icon"
                  icon="close"
                  onClick={this.props.onClose}
                />
              </div>
              <Heading size={700}>Give the gift of DISCO and save</Heading>
              <div className="Referral_Intro">
                Refer a friend to DISCO and if they sign up for a subscription,
                you’ll both get $20 credit
              </div>
            </div>
            <Row columnsSize={[12]}>
              <div>
                <img srcSet={`${img1x}, ${img2x} 2x`} />
              </div>
            </Row>
            <Row columnsSize={[12]} marginBottom="small">
              <div>
                <div className="Referral_Input" onKeyDown={this._onKeyDown}>
                  <TextField
                    placeholder="Enter an email address"
                    value={this.state.recipient.email}
                    onChange={this._handleEmailChange}
                  />
                  <Button
                    caption="Add email"
                    disabled={!this.state.recipient.email}
                    onClick={this._addRecipient}
                  />
                </div>
              </div>
            </Row>
            {this.props.recipients.length > 0 && (
              <Row columnsSize={[12]} marginBottom="small">
                <div>
                  <RecipientList
                    recipients={this.props.recipients}
                    onClick={this._deleteRecipient}
                  />
                </div>
              </Row>
            )}
            {this.state.error && (
              <Row columnsSize={[12]} marginBottom="small">
                <div>
                  <div className="Login_ValidationMessage">
                    {this.state.error}
                  </div>
                </div>
              </Row>
            )}
            {this._renderCustomMessage()}
          </div>
          <div className="Referral_Footer">
            <div>
              {this.state.isCopyStatusActive ? (
                this.state.copyStatusText
              ) : (
                <>
                  Or{' '}
                  <CopyToClipboard
                    className="Referral_Link"
                    data-clipboard-text={this._getReferralUrl()}
                    onSuccess={this._onCopySuccess}
                    onError={this._onCopyError}
                  >
                    copy your public referral link
                  </CopyToClipboard>{' '}
                  <HelpTooltip>
                    Use this when you’re posting it somewhere multiple people
                    might access it, like Slack, Facebook or what have you.
                  </HelpTooltip>
                </>
              )}
            </div>
            <div className="Referral_Actions">{this._renderSend()}</div>
          </div>
        </div>
      </DiscoModal>
    )
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ReferralInviteSendModal) as any
